import { GeoJsonFeatureFactory } from "../geojson/GeoJsonFeatureFactory";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import GeoJSON from 'ol/format/GeoJSON';
import * as olExtent from 'ol/extent';
import { TreeNode } from "@shared/ui-native/Tree";
import LayerGroup from 'ol/layer/Group';
import VectorTileLayer from "ol/layer/VectorTile";
import VectorTileSource from 'ol/source/VectorTile.js';

import { Auth } from "@shared/Auth";
import { ModelService } from "@shared/models/model-service";
import { transform as olTransform } from 'ol/proj';
import { GeojsonFeatureCollection } from '@shared/geojson/types';
import VectorTile from "ol/VectorTile";
import { GeoJsonUtils } from "../geojson/OlGeojsonUtils";
import geojsonvt from 'geojson-vt';
import { createXYZ, extentFromProjection } from 'ol/tilegrid'
import Projection from "ol/proj/Projection";
import { IDrawing } from "./drawing";
import { Ajax2 } from "@shared/Ajax";


const GEOJSON_FORMAT = new GeoJSON({
    featureProjection: 'EPSG:3857'
});

const projection = 'EPSG:3857';
const extent = extentFromProjection(projection);
const tileGrid =
    createXYZ({
        extent: extent,
        maxZoom: 22,
        tileSize: 512,
    });


const VECTOR_TILE_FORMAT = new GeoJSON({
    // Data returned from geojson-vt is in tile pixel units
    dataProjection: new Projection({
        code: 'TILE_PIXELS',
        units: 'tile-pixels',
        extent: [0, 0, 4096, 4096],
    }),
});

const BASE_URL: string = ModelService.getVizApiBaseUrl() + "/drawings";


export class VectorDrawing implements IDrawing {

    uuid: string;
    node: TreeNode;
    extent: olExtent.Extent;

    lineLayer: VectorTileLayer;
    textLayer: VectorLayer<any>;

    detailSource: VectorSource;
    detailLayer: VectorLayer<any>;
    polygonLayer: VectorLayer<any>;
    layerGroup: LayerGroup;

    tileIndex: any;

    constructor(node: TreeNode) {
        this.uuid = node.uuid;
        this.node = node;
        this.layerGroup = new LayerGroup({});
        this.extent = olExtent.createEmpty();
    }


    public async load() {
        this.createLineLayer().then(() => {
            if (this.lineLayer) {
                this.layerGroup.getLayers().push(this.lineLayer);
            }
        });

        this.createPolygonLayer()
        .then(() => {
                if (this.polygonLayer) {
                    this.layerGroup.getLayers().push(this.polygonLayer);
                }
            });


        this.createTextLayer()
            .then(() => {
                if (this.textLayer) {
                    this.layerGroup.getLayers().push(this.textLayer);
                }
            });

        this.createDetailLayer()
            .then(() => {
                if (this.detailLayer) {
                    this.layerGroup.getLayers().push(this.detailLayer);
                }
            });

    }

    private async createLineLayer() {
        const url =  `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/lines`

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: {"Authorization": "Bearer " + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();
        this.tileIndex = geojsonvt(features, {
            maxZoom: 24,
            tolerance: 5,
            extent: 4096,
        });

        const vectorSource = new VectorTileSource({
            maxZoom: 24,
            tileUrlFunction: (tileCoord) => JSON.stringify(tileCoord),
            tileLoadFunction: this.vectorTileLoader,
        });


        this.lineLayer = new VectorTileLayer({
            source: vectorSource,
            maxZoom: 24,
            minZoom: 6,
            style: GeoJsonFeatureFactory.STYLE.polyStyle
        });

        await this.calculateExtent();
    }


    private async createDetailLayer() {
        const url =  `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/details`

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: {"Authorization": "Bearer " + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();

        const detailFeatures = GEOJSON_FORMAT.readFeatures(features, { featureProjection: 'EPSG:3857' });
        const detailSource = new VectorSource({
            format: GEOJSON_FORMAT,
            features: detailFeatures
        });

        this.detailLayer = new VectorLayer({
            source: detailSource,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
            minZoom: 25,
            maxZoom: 27,

        });
    }


    private async createPolygonLayer() {
        const url =  `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/polygons`

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: {"Authorization": "Bearer " + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();
        console.log(features);
        const polygonFeatures = GEOJSON_FORMAT.readFeatures(features, { featureProjection: 'EPSG:3857' });
        const polygonSource = new VectorSource({
            format: GEOJSON_FORMAT,
            features: polygonFeatures
        });

        this.polygonLayer = new VectorLayer({
            source: polygonSource,
            style: GeoJsonFeatureFactory.STYLE.polyStyle,
            minZoom: 16,
            maxZoom: 27,

        });
    }

    private async createTextLayer() {
        const url =  `${BASE_URL}/${kuura.getProjectUuid()}/${this.uuid}/text`

        const token = await Auth.getToken();
        const resp = await fetch(url, {
            headers: {"Authorization": "Bearer " + token.accessToken },
        });

        if (!resp.ok) {
            return;
        }

        const features: GeojsonFeatureCollection = await resp.json();

        const textFeatures = GEOJSON_FORMAT.readFeatures(features);

        const vectorSource = new VectorSource({
            features: textFeatures,
        });

        this.textLayer = new VectorLayer({
            source: vectorSource,
            style: GeoJsonFeatureFactory.STYLE.textStyle,
            minZoom: 9,
        });

    }



    private async calculateExtent(): Promise<void> {
        const modelBoundsResp = await Ajax2({
            url: '/kuura/ajax_model_bounds.json',
            params: {
                id: this.node.id,
            },
        })

        const {maxGeographicPoint, minGeographicPoint}: {maxGeographicPoint: ILatLonElevation, minGeographicPoint: ILatLonElevation} = modelBoundsResp;

        const maxCoord = [maxGeographicPoint.lon, maxGeographicPoint.lat];
        const minCoord = [minGeographicPoint.lon, minGeographicPoint.lat];

        const max = olTransform(maxCoord, "EPSG:4326", "EPSG:3857");
        const min = olTransform(minCoord, "EPSG:4326", "EPSG:3857");


        const mapExtent: olExtent.Extent = olExtent.createEmpty();
        olExtent.extendCoordinates(mapExtent, [min, max]);
        this.extent = mapExtent;
    }

    public dispose() {

        if (this.textLayer) {
            this.textLayer.dispose();
        }

        if (this.lineLayer) {
            this.lineLayer.dispose();
        }

        if (this.polygonLayer) {
            this.polygonLayer.dispose();
        }

        if (this.detailLayer) {
            this.detailLayer.dispose();
        }
    }


    private vectorTileLoader = (tile: VectorTile, url: string) => {
        const tileCoord = JSON.parse(url);

        const tileData = this.tileIndex.getTile(
            tileCoord[0],
            tileCoord[1],
            tileCoord[2]
        );

        const geojson = JSON.stringify(
            {
                type: 'FeatureCollection',
                features: tileData ? tileData.features : [],
            },
            GeoJsonUtils.replacer
        );

        const actualFeatures = VECTOR_TILE_FORMAT.readFeatures(geojson, {
            extent: tileGrid.getTileCoordExtent(tileCoord),
            featureProjection: 'EPSG:3857',
        });

        tile.setFeatures(actualFeatures);
    }

}
